(ns manage.variants-preview-panel.view.sidebar
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/FormControl$default" :as FormControl]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.string :as str]
            [common.widgets.checkbox :refer [checkbox]]
            [common.widgets.form-field :refer [form-field]]
            [common.widgets.input :refer [input]]
            [manage.adset-preview-panel.view.context-field :refer [context-field]]
            [manage.adset-preview-panel.view.sidebar :refer [preview-sidebar]]
            [manage.variants-preview-panel.variant-id-table.view :as variant-id-table]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :refer [$]]))

(def status-filters
  ["draft"
   "approved"
   "published"])

(defn create-status-filters [current-filter new-value]
  (if (nil? current-filter)
    (vector new-value)
    (if (some #(= new-value %) current-filter)
      (remove #(= new-value %) current-filter)
      (conj current-filter new-value))))

(defn- gallery-navigation
  "Hiccup of rendering navigating within the sidebar"
  []
  (let [variant-id @(rf/subscribe [:variants-preview.variants/current-variant-id])
        content-id @(rf/subscribe [:variants-preview.variants/content-id variant-id])]
    [:> Stack {:direction "row" :align-items "center" :justify-content "space-between"
               :gap 1 :mb 3}
     [:> Button {:on-click #(rf/dispatch [:variants-preview.variants/previous-variant])
                 :variant "text"
                 :disabled @(rf/subscribe [:variants-preview.variants/previous-button-disabled?])
                 :start-icon ($ Icon "chevron_left")}
      "Previous"]
     ($ input
        {:value (or content-id "")
         :readOnly true
         :sx #js {:width "10rem"}})
     [:> Button {:on-click #(rf/dispatch [:variants-preview.variants/next-variant])
                 :variant "text"
                 :disabled @(rf/subscribe [:variants-preview.variants/next-button-disabled?])
                 :end-icon ($ Icon "chevron_right")}
      "Next"]]))

(defn- variant-context-list
  "Hiccup for rendering the selected variant context list"
  []
  (let [variant-id @(rf/subscribe [:variants-preview.variants/current-variant-id])
        targeting-values @(rf/subscribe [:variants-preview.variants/targeting-values variant-id])]
    [:<>
     [:> Typography {:variant "h4" :sx {:mb 1}} "Targeting keys/conditions"]
     [:> Stack {:spacing 2}
      (for [{:keys [name value]} targeting-values]
        ($ form-field
           {:key (str name)
            :label name
            :data-testid name}
           (r/as-element
            [context-field
             {:value (first value)
              :disabled? true}])))]]))

(defn- strategy-list
  "Hiccup for rendering the strategy preview variant list"
  []
  (let [variant-id @(rf/subscribe [:variants-preview.variants/current-variant-id])
        variants-drop-down-options @(rf/subscribe [:variants-preview.variants/variants-drop-down-options])
        targeting-values @(rf/subscribe [:variants-preview.variants/targeting-values variant-id])
        local-drop-down-choices @(rf/subscribe [:variants-preview.variants/targeting-drop-down-choices variant-id])
        filters @(rf/subscribe [:variants-preview.variants-status/filtering-variant-statuses])
        variant-count (count @(rf/subscribe [:variants-preview.variants/targeting-display-variants-ids targeting-values]))
        total (count (:value @(rf/subscribe [:variants-preview.variants/values])))
        is-targeting-set? @(rf/subscribe [:variants-preview.variants/is-targeting-configuration-set?])]
    ($ :<>
       ($ Stack
          {:direction "row"
           :justifyContent "space-between"
           :alignItems "center"}
          ($ FormControl
             {:size "small"}
             ($ Select
                {:id "filter"
                 :size "small"
                 :onChange (fn [e]
                             (let [v (.. e -target -value)
                                   new-value (last v)]
                               (rf/dispatch [:variants-preview.variants-status/set-filtering-variant-status (create-status-filters filters new-value)])))
                 :value #js [filters]
                 :displayEmpty true
                 :multiple true
                 :renderValue (fn [] "Filter")
                 :sx #js {:minWidth "15rem"
                          :textTransform "none"
                          :p 0.5}}
                ($ Typography
                   {:sx #js {:color "text.subtle"}
                    :fontWeight 600
                    :padding 1
                    :key :status-header}
                   "STATUS")
                ($ Stack {:sx #js {:px 2}}
                   (for [x status-filters]
                     ($ checkbox
                        {:key x
                         :label (str/capitalize x)
                         :value (some #(= x %) filters)
                         :on-change #(rf/dispatch [:variants-preview.variants-status/set-filtering-variant-status (create-status-filters filters x)])})))))
          (when is-targeting-set?
            ($ Typography {:sx #js {:p 1}}
               (str "Viewing " variant-count " / " total))))
       ($ Typography
          {:variant "h4"
           :sx #js {:mb 1 :mt 3}}
          "Strategy listing")
       ($ Stack {:spacing 2}
          (for [{:keys [name value]} targeting-values]
            ($ form-field
               {:key (str name)
                :label "name"
                :data-testid name}
               (r/as-element
                [context-field
                 {:value (or (local-drop-down-choices name) (first value))
                  :on-change (fn [selected-value]
                               (rf/dispatch
                                [:variants-preview.variants/navigate-to-variant
                                 targeting-values
                                 name
                                 selected-value
                                 local-drop-down-choices]))
                  :values (vec (get variants-drop-down-options name))
                  :selected-component :menu-select
                  :stack-direction "column"
                  :type "option"}])))))))

(defn sidebar
  "Hiccup of rendering the whole sidebar"
  [mobile?]
  (let [sidebar-visible @(rf/subscribe [:variants-preview.core/value :sidebar-visible])
        display-mode @(rf/subscribe [:variants-preview.core/display-mode])
        currently-displayed @(rf/subscribe [:variants-preview.variants/currently-displayed])
        total @(rf/subscribe [:variants-preview.variants/total])]
    [preview-sidebar {:mobile? mobile?
                      :open sidebar-visible
                      :on-close #(rf/dispatch [:variants-preview.core/set-sidebar-visible false])
                      :title "Variant details"
                      :header-children [:> Stack {:spacing 1}
                                        [:> Select {:size "small"
                                                    :value display-mode
                                                    :on-change #(rf/dispatch [:variants-preview.core/change-display-mode (.. % -target -value)])}
                                         [:> MenuItem {:value :side-by-side} "Side by side"]
                                         [:> MenuItem {:value :gallery} "Gallery"]
                                         [:> MenuItem {:value :strategy} "Strategy"]]
                                        (when (= display-mode :side-by-side)
                                          [:span (str currently-displayed "/" total " displayed")])]}
     (cond
       (= display-mode :gallery)
       [:<>
        [gallery-navigation]
        [variant-context-list]]

       (= display-mode :strategy)
       [:<> [strategy-list]]

       (= display-mode :side-by-side)
       [:> Box {:m -3}
        ($ variant-id-table/table)])]))
